/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 22/04/22
 */

import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { Faculty } from '@/wpt-lib/interfaces/Faculty';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { isNullish } from '@/wpt-lib/utils/isNullish';
import { LoadingSpinner } from '@/components';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { LabeledSelect } from '@/components/FormInputField';
import { formatFaculty } from '@/wpt-lib/utils/formatName';
import InactiveFacultyPopover from '@/components/pages/Admin/Pickers/InactiveFacultyPopover';

interface FacultyPickerProps {
    assignment: Assignment;
    facultyId: number | undefined;
    setFacultyId(id: number): void;
}

const FacultyPicker = ({ facultyId, assignment, setFacultyId }: FacultyPickerProps) => {
    const { error, loading, responseData, makeRequest } = useGetRequest<ListResponse<Faculty>>();

    const [showInactive, setShowInactive] = useState(false);

    const faculties = useMemo(() => {
        const factulties =
            (assignment.isGlobalAdmin
                ? responseData?.items
                : assignment.adminFaculties?.map((admin) => admin.faculty)) || [];

        return factulties
            .filter((f) => showInactive || !f.inactive)
            .sort((a, b) => formatFaculty(a).localeCompare(formatFaculty(b)));
    }, [assignment, responseData, showInactive]);

    useAsyncLayoutEffect(async () => {
        if (assignment.isGlobalAdmin) {
            try {
                await makeRequest('faculty?all=true');
            } catch (e) {
                // handled by hook
            }
        }
    }, [assignment]);

    useLayoutEffect(() => {
        if (faculties.length && (isNullish(facultyId) || !faculties.some((f) => f.id === facultyId))) {
            setFacultyId(faculties[0].id);
        }
    }, [faculties]);

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error || !faculties) {
        return <SimpleAlert variant="subtle">Could not load faculties</SimpleAlert>;
    }

    return (
        <>
            <LabeledSelect
                label="Faculty/Division:"
                labelProps={{ fontWeight: 'bold' }}
                onChange={(ev) => setFacultyId(parseInt(ev.target.value))}
                value={facultyId}
                minW="140px"
                width="fit-content"
                controlProps={{ width: 'inherit' }}
            >
                {faculties.map((f) => (
                    <option value={`${f.id}`} key={f.id}>
                        {formatFaculty(f)}
                    </option>
                ))}
            </LabeledSelect>
            <InactiveFacultyPopover
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                style={{ marginLeft: '0', marginTop: '5px' }}
            />
        </>
    );
};

export default FacultyPicker;
